/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect } from "react";
import {
  Button,
  Checkbox,
  Input,
  makeStyles,
  tokens,
  Text,
  Label,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { useMsal } from "@azure/msal-react";
import { DataService } from "../../services/dataService";
import type { IProjektItem } from "../../models/IProjektItem";
import type { IDataItem } from "../../models/IDataItem";
import { formatDate } from "../../utils/format";
import { getCurrentRole } from "../../utils/role";
import { sharedStyles } from "../../utils/styles";

const useStyles = makeStyles({
  page: {
    paddingTop: "5px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "8px",
  },
  section: {
    padding: "24px",
    backgroundColor: tokens.colorNeutralBackground2, //'#f9f9f9',
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  title: {
    fontSize: "1.3rem",
    fontWeight: 600,
    lineHeight: "3",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "15px",
    paddingBottom: "8px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
      gap: "12px",
      paddingBottom: "12px",
    },
  },
  field: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    display: "flex",
    alignItems: "center",
    paddingBottom: "3px",
  },
  input: sharedStyles.input,
  inputSmall: {
    ...sharedStyles.input,
    minHeight: "40px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    marginTop: "5px",
    paddingTop: "5px",
    "@media (max-width: 768px)": {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  actionButton: {
    width: "250px",
    "@media (max-width: 768px)": {
      width: "100%",
      minWidth: 0,
    },
  },
});

interface IProjektdatenProps {
  onClose?: () => void;
  typ?: string;
  format?: string;
}

const Projektdaten: React.FC<IProjektdatenProps> = ({
  onClose,
  typ,
  format,
}) => {
  const panelTopRef = React.useRef<HTMLDivElement>(null);
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [roles, setRoles] = useState<string[]>(getCurrentRole());
  const styles = useStyles();
  const [projektname, setProjektname] = useState("");
  const [studiengangsbezeichnung, setStudiengangsbezeichnung] = useState("");
  const [institution, setInstitution] = useState("");
  const [studienfach, setStudienfach] = useState("");
  const [studiengangsstart, setStudiengangsstart] = useState<Date | undefined>(
    undefined,
  );
  const [organisationsstruktur, setOrganisationsstruktur] = useState("");
  const [gesamtumfang, setGesamtumfang] = useState(0);
  const [studiendauer, setStudiendauer] = useState(0);
  const [unterrichtssprache, setUnterrichtssprache] = useState("");
  const [akademischerGrad, setAkademischerGrad] = useState("");
  const [anzahlStudienplaetze, setAnzahlStudienplaetze] = useState(0);
  const [standort, setStandort] = useState("");
  const [qualifikationsniveau, setQualifikationsniveau] = useState("");
  const [zulassungsvoraussetzungen, setZulassungsvoraussetzungen] =
    useState("");
  const [personen, setPersonen] = useState([] as IDataItem[]);
  const [selectedPerson, setSelectedPerson] = useState<string>("");

  const [finanz, setFinanz] = useState([] as IDataItem[]);
  const [selectedPersonFinanz, setSelectedPersonFinanz] = useState<string>("");

  const [freigeber, setFreigeber] = useState([] as IDataItem[]);
  const [selectedPersonFreigeber, setSelectedPersonFreigeber] =
    useState<string>("");

  const [controlling, setControlling] = useState([] as IDataItem[]);
  const [selectedPersonControlling, setSelectedPersonControlling] =
    useState<string>("");

  const [inhalt, setInhalt] = useState([] as IDataItem[]);
  const [selectedPersonInhalt, setSelectedPersonInhalt] = useState<string>("");

  const [studiengangsleitung, setStudiengangsleitung] = useState(
    [] as IDataItem[],
  );
  const [selectedPersonStg, setSelectedPersonStg] = useState<string>("");

  const [interimistisch, setInterimistisch] = useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [color, setColor] = useState("");

  useEffect(() => {
    const handleRoleChange = () => {
      console.log("Dashboard detected role change event");
      const newRoles = getCurrentRole();
      console.log("New roles after event:", newRoles);
      setRoles(newRoles);
    };

    console.log("Dashboard mounted, initial roles:", roles);

    const currentRoles = getCurrentRole();
    if (currentRoles.length > 0 && roles.length === 0) {
      console.log(
        "Role was set before event listener - updating now:",
        currentRoles,
      );
      setRoles(currentRoles);
    }

    window.addEventListener("role-changed", handleRoleChange);
    window.addEventListener("storage", handleRoleChange);

    return () => {
      window.removeEventListener("role-changed", handleRoleChange);
      window.removeEventListener("storage", handleRoleChange);
    };
  }, [roles]);

  const scrollToTopOfPanel = () => {
    setTimeout(() => {
      if (panelTopRef.current) {
        // First, use scrollIntoView immediately
        panelTopRef.current.scrollIntoView({
          behavior: "auto",
          block: "start",
        });

        // Then find and scroll the scrollable parent to ensure we're at the top
        let element = panelTopRef.current.parentElement;
        while (element) {
          const overflowY = window.getComputedStyle(element).overflowY;
          const isScrollable = overflowY === "scroll" || overflowY === "auto";
          if (isScrollable && element.scrollHeight > element.clientHeight) {
            element.scrollTop = 0;
            break;
          }
          element = element.parentElement;
        }
      }
    }, 150);
  };

  useEffect(() => {
    const getPersonen = async (art: string) => {
      if (!dataService) return;
      const response = await dataService.getRelevantePersonen(art);
      if (response) {
        if (art === "Entwickler*in") {
          setPersonen(response);
        }
        if (art === "Freigeber*in") {
          setFreigeber(response);
        }
        if (art === "Controlling") {
          setControlling(response);
        }
        if (art === "Hochschulleitung") {
          setFinanz(response);
        }
        if (art === "Kollegium") {
          setInhalt(response);
        }
        if (art === "Studiengangsleiter*in") {
          setStudiengangsleitung(response);
        }
      }
    };
    getPersonen("Entwickler*in");
    getPersonen("Freigeber*in");
    getPersonen("Controlling");
    getPersonen("Hochschulleitung");
    getPersonen("Kollegium");
    getPersonen("Studiengangsleiter*in");
  }, [dataService]);

  const checkRequiredFields = () => {
    let result = false;
    result =
      projektname.trim() === "" ||
      studiengangsbezeichnung.trim() === "" ||
      institution.trim() === "" ||
      studienfach.trim() === "" ||
      studiengangsstart === undefined ||
      organisationsstruktur.trim() === "" ||
      gesamtumfang === 0 ||
      studiengangsstart === undefined ||
      unterrichtssprache.trim() === "" ||
      akademischerGrad.trim() === "" ||
      anzahlStudienplaetze === 0 ||
      standort.trim() === "" ||
      qualifikationsniveau.trim() === "" ||
      zulassungsvoraussetzungen.trim() === "" ||
      selectedPerson.trim() === "" ||
      selectedPersonFreigeber.trim() === "" ||
      selectedPersonInhalt.trim() === "" ||
      selectedPersonFinanz.trim() === "" ||
      selectedPersonControlling.trim() === "" ||
      selectedPersonStg.trim() === "";
    return result;
  };

  const saveProjekt = async () => {
    setErrorMessage("");
    if (!dataService) return;
    if (checkRequiredFields()) {
      setErrorMessage("Bitte füllen Sie alle erforderlichen Felder aus.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }
    await dataService
      .addUpdateProjekt({
        typ: typ,
        art: format,
        bezeichnung: projektname,
        studiengang: studiengangsbezeichnung,
        institution: institution,
        studienfach: studienfach,
        studiengangsstart: studiengangsstart,
        organisationsstruktur: organisationsstruktur,
        ects: gesamtumfang,
        unterrichtssprache: unterrichtssprache,
        akadGrad: akademischerGrad,
        studienplaetze: anzahlStudienplaetze,
        standort: standort,
        qualifikationsniveau: qualifikationsniveau,
        zulassungsvoraussetzungen: zulassungsvoraussetzungen,
        studiendauer: studiendauer,
        entwicklerUpn: selectedPerson,
        freigabeUpn: selectedPersonFreigeber,
        inhaltFreigabeUpn: selectedPersonInhalt,
        finanzFreigabeUpn: selectedPersonFinanz,
        controllingUpn: selectedPersonControlling,
        studiengangsleitungUpn: selectedPersonStg,
        interimistisch: interimistisch,
        statusId: 1,
      } as IProjektItem)
      .then((_result) => {
        setErrorMessage("Daten erfolgreich gespeichert");
        setColor("green");
        scrollToTopOfPanel();
        setTimeout(() => {
          if (onClose) {
            onClose();
          }
        }, 2000);
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage("Fehler beim Speichern der Projektdaten");
        setColor("red");
        scrollToTopOfPanel();
      });
  };

  const _sendClicked = () => {
    void saveProjekt();
  };

  return (
    <>
      <div ref={panelTopRef}>
        {errorMessage && (
          <div
            style={{
              fontSize: "16px",
              marginBottom: "30px",
              maxWidth: "500px",
              color: color,
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
      <hr
        style={{
          height: "2px",
          borderWidth: 0,
          color: "gray",
          backgroundColor: "gray",
        }}
      />
      <div className={styles.page}>
        <div className={styles.section}>
          <Label
            htmlFor="Projektname"
            className={styles.label}
            required
            style={{ paddingTop: "0px" }}
          >
            Projektname
          </Label>
          <Input
            className={styles.input}
            id="Projektname"
            value={projektname}
            autoFocus
            required
            onChange={(e) =>
              e.target.value.length <= 500 && setProjektname(e.target.value)
            }
          />
          <Text className={styles.title}>Projektdetails</Text>

          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label
                htmlFor="Studiengangsbezeichnung"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Studiengangsbezeichnung
              </Label>
              <Input
                className={styles.input}
                id="Studiengangsbezeichnung"
                value={studiengangsbezeichnung}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 500 &&
                  setStudiengangsbezeichnung(e.target.value)
                }
              />
            </div>
            <div className={styles.field}>
              <Label
                htmlFor="Institution"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Institution
              </Label>
              <Input
                className={styles.input}
                id="Institution"
                value={institution}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 500 && setInstitution(e.target.value)
                }
              />
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label
                htmlFor="Studienfach"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Studienfach
              </Label>
              <Input
                className={styles.input}
                id="Studienfach"
                value={studienfach}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 500 && setStudienfach(e.target.value)
                }
              />
            </div>
            <div className={styles.field}>
              <Label
                htmlFor="Studiengangsstart"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Studiengangsstart
              </Label>
              <DatePicker
                className={styles.input}
                id="Studiengangsstart"
                value={studiengangsstart}
                autoFocus
                required
                formatDate={(date) => (date ? formatDate(date) : "")}
                onSelectDate={(date) => date && setStudiengangsstart(date)}
              />
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label
                htmlFor="Organisationsstruktur"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Organisationsstruktur
              </Label>
              <Input
                className={styles.input}
                id="Organisationsstruktur"
                value={organisationsstruktur}
                autoFocus
                onChange={(e) => setOrganisationsstruktur(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <Label
                htmlFor="Gesamtumfang"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Gesamtumfang
              </Label>
              <Input
                className={styles.input}
                id="Gesamtumfang"
                value={gesamtumfang.toString()}
                autoFocus
                type="number"
                min={0}
                max={500}
                onChange={(e) =>
                  e.target.value !== "" &&
                  setGesamtumfang(Number(e.target.value))
                }
              />
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label
                htmlFor="Studiendauer"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Studiendauer
              </Label>
              <Dropdown
                className={styles.input}
                id="Studiendauer"
                value={studiendauer.toString()}
                autoFocus
                onOptionSelect={(_e, option) =>
                  option && setStudiendauer(Number(option.optionValue))
                }
              >
                <Option value="0">0</Option>
                <Option value="1">1</Option>
                <Option value="2">2</Option>
                <Option value="3">3</Option>
                <Option value="4">4</Option>
                <Option value="5">5</Option>
                <Option value="6">6</Option>
                <Option value="7">7</Option>
                <Option value="8">8</Option>
                <Option value="9">9</Option>
                <Option value="10">10</Option>
              </Dropdown>
            </div>
            <div className={styles.field}>
              <Label
                htmlFor="Unterrichtssprache"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Unterrichtssprache
              </Label>
              <Input
                className={styles.input}
                id="Unterrichtssprache"
                value={unterrichtssprache}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 50 &&
                  setUnterrichtssprache(e.target.value)
                }
              />
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label
                htmlFor="AkademischerGrad"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Akademischer Grad (inkl. Abkürzung)
              </Label>
              <Input
                className={styles.input}
                id="AkademischerGrad"
                value={akademischerGrad}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 50 &&
                  setAkademischerGrad(e.target.value)
                }
              />
            </div>
            <div className={styles.field}>
              <Label
                htmlFor="Studienplaetze"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Anzahl der Studienplätze
              </Label>
              <Input
                className={styles.input}
                id="Studienplaetze"
                type="number"
                min="0"
                max="500"
                value={anzahlStudienplaetze.toString()}
                autoFocus
                onChange={(e) =>
                  setAnzahlStudienplaetze(Number(e.target.value))
                }
              />
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label
                htmlFor="Programmstandort"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Programmstandort
              </Label>
              <Input
                className={styles.input}
                id="Programmstandort"
                value={standort}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 500 && setStandort(e.target.value)
                }
              />
            </div>
            <div className={styles.field}>
              <Label
                htmlFor="Qualifikationsniveau"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Qualifikationsniveau
              </Label>
              <Input
                className={styles.input}
                id="Qualifikationsniveau"
                value={qualifikationsniveau}
                autoFocus
                onChange={(e) => setQualifikationsniveau(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.field}>
            <Label
              htmlFor="Zulassungsvoraussetzungen"
              className={styles.label}
              required
              style={{ paddingTop: "0px" }}
            >
              Zulassungsvoraussetzungen
            </Label>
            <Input
              className={styles.input}
              id="Zulassungsvoraussetzungen"
              value={zulassungsvoraussetzungen}
              autoFocus
              onChange={(e) => setZulassungsvoraussetzungen(e.target.value)}
            />
          </div>
          <Text className={styles.title}>Relevante Personen</Text>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label
                htmlFor="Entwickler"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Entwickler*in
              </Label>
              <Dropdown
                placeholder="User wählen..."
                value={
                  personen.find((role) => role.value === selectedPerson)
                    ?.text || ""
                }
                selectedOptions={selectedPerson ? [selectedPerson] : []}
                onOptionSelect={(_, data) => {
                  setSelectedPerson(data.optionValue || "");
                }}
              >
                {personen.map((role) => (
                  <Option key={role.text} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.field}>
              <Label
                htmlFor="Freigeber"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Freigeber*in
              </Label>
              <Dropdown
                placeholder="User wählen..."
                value={
                  freigeber.find(
                    (role) => role.value === selectedPersonFreigeber,
                  )?.text || ""
                }
                selectedOptions={
                  selectedPersonFreigeber ? [selectedPersonFreigeber] : []
                }
                onOptionSelect={(_, data) => {
                  setSelectedPersonFreigeber(data.optionValue || "");
                }}
              >
                {freigeber.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label
                htmlFor="Studiengangsleitung"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Studiengangsleitung
              </Label>
              <Dropdown
                placeholder="User wählen..."
                value={
                  studiengangsleitung.find(
                    (role) => role.value === selectedPersonStg,
                  )?.text || ""
                }
                selectedOptions={selectedPersonStg ? [selectedPersonStg] : []}
                onOptionSelect={(_, data) => {
                  setSelectedPersonStg(data.optionValue || "");
                }}
              >
                {studiengangsleitung.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
              <div>
                <Checkbox
                  label="Interimistisch"
                  checked={interimistisch}
                  onChange={(_, data) =>
                    setInterimistisch(data.checked === true)
                  }
                />
              </div>
            </div>
            <div className={styles.field}>
              <Label
                htmlFor="Controlling"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Controlling
              </Label>
              <Dropdown
                placeholder="User wählen..."
                value={
                  controlling.find(
                    (role) => role.value === selectedPersonControlling,
                  )?.text || ""
                }
                selectedOptions={
                  selectedPersonControlling ? [selectedPersonControlling] : []
                }
                onOptionSelect={(_, data) => {
                  setSelectedPersonControlling(data.optionValue || "");
                }}
              >
                {controlling.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label
                htmlFor="InhaltFreigabe"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Inhaltliche Freigabe
              </Label>
              <Dropdown
                placeholder="User wählen..."
                value={
                  inhalt.find((role) => role.value === selectedPersonInhalt)
                    ?.text || ""
                }
                selectedOptions={
                  selectedPersonInhalt ? [selectedPersonInhalt] : []
                }
                onOptionSelect={(_, data) => {
                  setSelectedPersonInhalt(data.optionValue || "");
                }}
              >
                {inhalt.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.field}>
              <Label
                htmlFor="FinanzFreigabe"
                className={styles.label}
                required
                style={{ paddingTop: "0px" }}
              >
                Finanzielle Freigabe
              </Label>
              <Dropdown
                placeholder="User wählen..."
                value={
                  finanz.find((role) => role.value === selectedPersonFinanz)
                    ?.text || ""
                }
                selectedOptions={
                  selectedPersonFinanz ? [selectedPersonFinanz] : []
                }
                onOptionSelect={(_, data) => {
                  setSelectedPersonFinanz(data.optionValue || "");
                }}
              >
                {finanz.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
          </div>
          <div className={styles.footer}>
            <Button
              appearance="primary"
              className={styles.actionButton}
              disabled={checkRequiredFields()}
              onClick={_sendClicked}
            >
              Projekt erstellen
            </Button>
            <Button className={styles.actionButton} onClick={onClose}>
              Abbrechen
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Projektdaten;
